import React, { useState } from "react";
import PropTypes from "prop-types";
import { SIGNOL_COLOUR_NAMES } from "@signolteam/shared/colours";
import AppUtils from "@src/utils/appUtils";
import HorizontalLayout from "@src/components/cards/HorizontalLayout";
import Button from "@src/components/atoms/Button";
import Checkbox from "@src/components/atoms/Checkbox";
import CardContainer from "@src/components/cards/CardContainer";

const SupportForm = ({ onSubmit }) => {
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const minLength = 2;
  const maxLength = 2000;

  const validateContent = (content) => {
    return RegExp(/^[A-Za-z0-9\/\s\.,?'-]*$/).test(content);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      // Validation
      if (comment.length < minLength) {
        setError(`Comment must be at least ${minLength} characters`);
        return;
      }

      if (comment.length > maxLength) {
        setError(`Comment must be less than ${maxLength} characters`);
        return;
      }

      if (!validateContent(comment)) {
        setError(
          `Comments can only contain letters, numbers and some special characters ( / . - , ' ? )`,
        );
        return;
      }

      await onSubmit(comment);
      setSubmitted(true);
    } catch (err) {
      setError("Failed to submit support request. Please try again.");
      console.error("Submission error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCommentChange = (e) => {
    const value = e.target.value;
    if (value === "" || validateContent(value)) {
      setComment(value);
    }
  };

  return (
    <CardContainer bgColor={"white"}>
      <div className="flex flex-col justify-center items-center h-full w-full p-4">
        <div className="w-full sm:w-3/4">
          {!!submitted && (
            <div className="p-4 text-center">
              <p className="text-lg font-medium">
                Thank you for sharing. We will get back to you as soon as we
                can.
              </p>
            </div>
          )}
          {!submitted && (
            <div className="text-center w-full">
              <h1 className=" font-extrabold mb-4">Support</h1>
              <div className="text-sm text-left">
                We're always happy to resolve questions or learn from feedback.
                Reach out using the support form and we'll respond to you in the
                next 3 working days.
              </div>
              <form onSubmit={handleSubmit} className="mt-4">
                <textarea
                  value={comment}
                  onChange={handleCommentChange}
                  className={`bg-white border-1 border-gray-500 text-sm text-area mx-auto p-2 mb-1 w-full rounded-md min-h-36 md:min-h-16 ${error ? " text-red-500 bg-red-200" : ""}`}
                  rows={8}
                  placeholder={"Let us know how we can help."}
                  maxLength={maxLength}
                />
                {error && (
                  <div className="mt-4 text-xs italic text-red-500">
                    {error}
                  </div>
                )}

                <div className={"flex items-center"}>
                  <Button
                    submit={true}
                    disabled={!comment}
                    loading={isSubmitting}
                    className={`px-4 py-2`}
                  >
                    Submit
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </CardContainer>
  );
};

export const supportFormControls = {
  color: {
    propType: PropTypes.string,
    description: "Background color",
    controlType: "select",
    options: SIGNOL_COLOUR_NAMES,
  },
  existingComment: {
    propType: PropTypes.string,
    description: "Existing comment that the user submitted in the past",
    controlType: "text",
  },
  preferredEventName: {
    propType: PropTypes.string,
    description: "Preferred event name",
    controlType: "select",
    options: ["opportunity", "day", "event"],
  },
  onSubmit: {
    propType: PropTypes.func,
    description: "Submit function to save the comment",
    disable: true,
  },
};

SupportForm.propTypes = AppUtils.getPropTypeFromControls(supportFormControls);

export default SupportForm;
